import '../css/app.css';

import { createInertiaApp, router } from '@inertiajs/react';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import * as Sentry from '@sentry/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from './components/ui/sonner';
import { initializeTheme } from './hooks/use-appearance';
import analytics from './lib/analytics';
import registerFlashToasts from './lib/flashToaster';
import { initSentry } from './lib/sentry';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

// Initialize Sentry
initSentry();

// Catch Inertia exceptions
router.on('networkError', (event) => {
    Sentry.captureException(event.detail.error);
});

registerFlashToasts();

// Initialize Google Analytics (only if GA ID is available)
if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
    analytics.initialize().catch(console.error);
}

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) =>
        resolvePageComponent(
            `./pages/${name}.tsx`,
            import.meta.glob<{ default: ComponentType }>(
                './pages/**/*.tsx',
            ),
        ).then((module) => module.default),
    setup({ el, App, props }) {
        const root = createRoot(el);

        root.render(
            <StrictMode>
                <Sentry.ErrorBoundary
                    fallback={({ error, resetError }) => {
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : 'An unexpected error occurred';

                        return (
                            <div className="flex min-h-screen items-center justify-center">
                                <div className="text-center">
                                    <h1 className="mb-4 text-2xl font-bold">
                                        Something went wrong
                                    </h1>
                                    <p className="mb-4 text-gray-600">
                                        {errorMessage}
                                    </p>
                                    <button
                                        onClick={resetError}
                                        className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                                    >
                                        Try again
                                    </button>
                                </div>
                            </div>
                        );
                    }}
                >
                    <PayPalScriptProvider
                        options={{
                            currency: import.meta.env.VITE_PAYPAL_CURRENCY,
                            clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID,
                            vault: 'true',
                            intent: 'subscription',
                        }}
                    >
                        <Toaster position="top-center" />
                        <App {...props} />
                    </PayPalScriptProvider>
                </Sentry.ErrorBoundary>
            </StrictMode>,
        );
    },
    progress: {
        color: '#4B5563',
    },
});

// This will set light / dark mode on load...
initializeTheme();
